import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { Icon, type IconName } from './Icon';

/** How loud the button is. There is no fifth option on purpose. */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  /** Visual weight. Defaults to `secondary`: most buttons are not the point of the screen. */
  readonly variant?: ButtonVariant;
  /** `sm` for dense toolbars, `md` everywhere else. */
  readonly size?: 'sm' | 'md';
  /** A glyph before the label. */
  readonly icon?: IconName;
  /**
   * Renders the glyph alone. The `children` are then used as the accessible
   * name and as the tooltip, so an icon-only button is never unnamed.
   */
  readonly iconOnly?: boolean;
  /** Stretches to the width of the container. */
  readonly block?: boolean;
  /**
   * Marks work in progress: the control stays focusable and keeps its width,
   * and its busy state is announced rather than merely drawn.
   */
  readonly busy?: boolean;
  /** The label. Required — an unlabelled button is a bug, not a variant. */
  readonly children: ReactNode;
}

/**
 * The button.
 *
 * Two decisions worth stating. First, `busy` does not disable: a disabled
 * element loses focus and drops out of the tab order mid-interaction, which
 * strands a keyboard user at an unpredictable place in the page, so a busy
 * button stays focusable and refuses the click instead. It is also
 * `aria-disabled`, so assistive technology reports it as unavailable and a
 * test driver waits for it rather than clicking a control that would swallow
 * the click. Second, `iconOnly`
 * still requires children, because the accessible name has to come from
 * somewhere and a required prop is the only way to be sure it did.
 *
 * @param props see {@link ButtonProps}
 * @returns the button element
 */
export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  iconOnly = false,
  block = false,
  busy = false,
  type = 'button',
  children,
  disabled = false,
  onClick,
  ...rest
}: ButtonProps): React.JSX.Element {
  const label = iconOnly && typeof children === 'string' ? children : undefined;

  return (
    <button
      {...rest}
      type={type}
      className="fo-button"
      data-variant={variant}
      data-size={size}
      data-block={block ? '' : undefined}
      data-icon-only={iconOnly ? '' : undefined}
      data-busy={busy ? '' : undefined}
      disabled={disabled}
      aria-busy={busy || undefined}
      aria-disabled={busy ? true : rest['aria-disabled']}
      aria-label={rest['aria-label'] ?? label}
      title={label}
      onClick={(event) => {
        if (busy) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
    >
      {icon !== undefined ? <Icon name={icon} className="fo-button__icon" /> : null}
      {iconOnly ? null : <span className="fo-button__label">{children}</span>}
    </button>
  );
}
